import { useEffect, useState } from "react";
import {
  Container,
  Typography,
  Paper,
  TextField,
  Button,
  Grid,
  Box,
  Chip,
  Stack,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  TablePagination,
  Autocomplete,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { supabase } from "../lib/supabaseClient";
import { useAuth } from "../context/AuthContext";
import * as XLSX from "xlsx";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import EditIcon from "@mui/icons-material/Edit";
import SchoolIcon from "@mui/icons-material/School";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import SearchIcon from "@mui/icons-material/Search";
import DownloadIcon from "@mui/icons-material/Download";
import GroupsIcon from "@mui/icons-material/Groups";
import { findSimilarStudent, calculateSimilarity } from "../utils/similarity";
import { hourlyRateRule, isValidHourlyRate, maxRateFor } from "../utils/rates";

// How each planned change is labelled in the review list.
const CHANGE_LABELS = {
  update: { text: "ADD HOURS", color: "success", tint: "inherit" },
  remove: { text: "REMOVE HOURS", color: "default", tint: "rgba(0, 0, 0, 0.04)" },
  "new-pairing": {
    text: "NEW LINK",
    color: "info",
    tint: "rgba(2, 136, 209, 0.06)",
  },
  "new-student": {
    text: "NEW STUDENT",
    color: "warning",
    tint: "rgba(237, 108, 2, 0.08)",
  },
  error: { text: "ERROR", color: "error", tint: "rgba(211, 47, 47, 0.06)" },
};

// `findSimilarStudent` returns the FIRST student over the threshold, not the
// best one. That was tolerable when a near-miss only produced a warning, but
// this flow now creates a student when nothing matches — so a mediocre early
// name shadowing a strong later one would silently insert a duplicate. Scan
// the whole list and keep the highest score, the way the tutor lookup does.
const findClosestStudent = (name, students, normalize) => {
  const target = normalize(name);
  let best = null;

  for (const student of students) {
    const similarity = calculateSimilarity(target, normalize(student.full_name));
    if (!best || similarity > best.similarity) {
      best = { student, similarity };
    }
  }

  return best && best.similarity >= 0.6 ? best : null;
};

// One short, plain sentence per row. The Student, Tutor, Hours and Rate
// columns already carry the numbers, so this only has to say what happens —
// repeating the names and figures here just makes the row harder to scan.
const describePendingRow = (row) => {
  if (row.changeType !== "error" && row.isRemoval) {
    return `Removes ${Math.abs(row.newHours)} hrs. Rate unchanged.`;
  }

  switch (row.changeType) {
    case "error":
      return row.message;
    case "new-student":
      return "New student. Creates the student, then links them to this tutor.";
    case "new-pairing":
      return "New tutor for this student. Creates the link.";
    default: {
      // An existing package takes the sheet's rate, so a rate change is
      // spelled out in words — it is easy to miss in the Rate column, and
      // it is the part that quietly changes what a tutor gets paid.
      const rateChanged =
        row.rateBefore !== null && row.rateBefore !== row.rate;

      return rateChanged
        ? `Adds ${row.newHours} hrs. Rate changed to R${row.rate}.`
        : `Adds ${row.newHours} hrs.`;
    }
  }
};

export default function StudentsPage() {
  const { profile } = useAuth();
  // The viewer role is read-only: the lists, search and downloads stay, every
  // control that writes is hidden. RLS blocks those writes regardless.
  const readOnly = profile?.role === "viewer";
  const [loading, setLoading] = useState(false);

  // Data State
  const [students, setStudents] = useState([]);
  const [tutors, setTutors] = useState([]);

  // Modal & Search/Pagination State
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const [openCreditDialog, setOpenCreditDialog] = useState(false);
  const [adjustmentValue, setAdjustmentValue] = useState(0);
  const [openStudentNameDialog, setOpenStudentNameDialog] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [newStudentNameValue, setNewStudentNameValue] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [uploadingHours, setUploadingHours] = useState(false);
  const [applyingUpload, setApplyingUpload] = useState(false);
  // Holds the parsed-but-not-yet-written result of a bulk hours upload.
  // Nothing is written to the database until an admin approves this batch,
  // so the review panel is a true dry run of what the sheet would do.
  const [pendingUpload, setPendingUpload] = useState(null);
  const [bulkUploadDialog, setBulkUploadDialog] = useState({
    open: false,
    title: "",
    message: "",
    severity: "info",
    rows: [],
  });

  // Form State
  const [studentName, setStudentName] = useState("");
  const [assignment, setAssignment] = useState({
    studentId: "",
    tutorId: "",
    rate: "",
    hoursBought: "",
  });

  useEffect(() => {
    fetchData();
  }, []);

  async function fetchData() {
    setLoading(true);
    try {
      // Fetch students with their tutor assignments and lesson logs, and fetch tutors separately for mapping
      const [studentsRes, tutorsRes] = await Promise.all([
        supabase
          .from("students")
          .select(
            `
            *,
            student_tutor_assignments (
              id,
              tutor_id,
              rate,
              hours_bought,
              tutors (
                profiles (full_name)
              )
            ),
            lesson_logs ( hours, status, student_id, tutor_id )
          `,
          )
          .order("full_name", { ascending: true }),
        supabase
          .from("tutors")
          .select(`id, max_hourly_rate, profiles (full_name)`),
      ]);

      if (studentsRes.error) throw studentsRes.error;

      const formattedStudents = (studentsRes.data || []).map((s) => {
        // Calculate usage per assignment
        const assignmentsWithUsage = s.student_tutor_assignments?.map((asg) => {
          const used =
            s.lesson_logs
              ?.filter(
                (log) =>
                  (log.status === "approved" || log.status === "pending") &&
                  log.student_id === s.id &&
                  log.tutor_id === asg.tutor_id,
              )
              .reduce((acc, log) => acc + (Number(log.hours) || 0), 0) || 0;

          return { ...asg, usedHours: used };
        });

        return { ...s, assignments: assignmentsWithUsage };
      });

      setStudents(formattedStudents);
      setTutors(tutorsRes.data || []);
    } catch (error) {
      console.error("Error fetching data:", error.message);
    } finally {
      setLoading(false);
    }
  }

  // The rate ceiling depends on the tutor picked in the link form - most are
  // R240, a tutor with an approved exception has their own (utils/rates.js).
  const assignMaxRate = maxRateFor(
    tutors.find((t) => t.id === assignment.tutorId),
  );

  const isValidHoursAmount = (value) => {
    const hours = Number(value);
    return Number.isFinite(hours) && hours > 0;
  };

  // The upload also accepts negative hours, for moving hours off one tutor and
  // onto another (a student was tutored by someone other than the tutor the
  // hours were bought against). Zero is still meaningless, and a blank cell
  // reads as zero, so it stays rejected.
  const isValidUploadHours = (value) => {
    const hours = Number(value);
    return Number.isFinite(hours) && hours !== 0;
  };

  const handleDownloadBulkHoursTemplate = () => {
    const workbook = XLSX.utils.book_new();

    const worksheet = XLSX.utils.aoa_to_sheet([
      ["Student", "Tutor", "Number of New Hours", "Price Per Hour"],
    ]);

    worksheet["!cols"] = [
      { wch: 28 },
      { wch: 24 },
      { wch: 24 },
      { wch: 20 },
    ];

    XLSX.utils.book_append_sheet(workbook, worksheet, "Bulk Hours Upload");

    const instructionsRows = [
      ["Bulk Upload Rules"],
      [""],
      ["The Tutor must already exist in the system."],
      [
        "The Student does NOT have to exist. A student who is not on file is flagged NEW STUDENT and is created when you approve the upload.",
      ],
      [
        "The Student does NOT have to be linked to the Tutor yet. A missing link is flagged NEW LINK and is created when you approve the upload.",
      ],
      ["Number of New Hours is added to the hours the student already has."],
      [""],
      ["MOVING HOURS BETWEEN TUTORS"],
      [
        "Number of New Hours may be NEGATIVE, to take hours off a tutor. Use this when hours were bought against one tutor but a different tutor did the teaching.",
      ],
      [
        "To move 2 hours from James to Timmy, use two rows: one for James with -2, and one for Timmy with 2.",
      ],
      [
        "You can only remove hours from a Student and Tutor who are already linked.",
      ],
      [
        "You cannot remove hours a tutor has already logged lessons against. The review screen tells you the lowest number you can go to.",
      ],
      [
        "Removing hours never changes the rate, so Price Per Hour may be left blank on a negative row.",
      ],
      [""],
      [
        "The same Student and Tutor may appear on more than one row. The hours are added together.",
      ],
      [
        "When they do, every one of those rows must show the SAME Price Per Hour. Different rates for the same pair is an error, because a pair only has one rate.",
      ],
      [
        "For a new student or a new link, Number of New Hours becomes the starting hours.",
      ],
      ["Price Per Hour must be a whole number between R130 and R240."],
      [
        "A tutor with an approved rate exception may go higher, up to their own maximum (for example Rocio Steenberg, up to R400).",
      ],
      ["Price Per Hour must be a multiple of 10."],
      ["Examples of valid rates: 130, 150, 160, 200, 240."],
      ["Examples of invalid rates: 120, 149.99, 155, 225, 300."],
      [""],
      [
        "Nothing is saved when you upload. The file is checked first, then every change is listed on the Students page for you to Approve or Deny.",
      ],
      [
        "If any row has an error, the whole batch cannot be approved. Fix the sheet and upload it again.",
      ],
      [
        "Full stops, hyphens, apostrophes and extra spaces are ignored when names are matched. 'Dr Tutor' and 'Dr. Tutor' are the same tutor.",
      ],
      [
        "A misspelt name that is still at least 90% the same is matched automatically, and the review list shows what it was read as.",
      ],
      [
        "Below 90%, a student name creates a NEW STUDENT. Spell student names carefully or you will create a duplicate instead of adding hours to the right person.",
      ],
    ];

    const instructionsSheet = XLSX.utils.aoa_to_sheet(instructionsRows);
    instructionsSheet["!cols"] = [{ wch: 85 }];

    XLSX.utils.book_append_sheet(workbook, instructionsSheet, "Instructions");
    XLSX.writeFile(workbook, "Bulk_Hours_Upload_Template.xlsx");
  };

  // Exports the current tutor and student name lists as two separate,
  // unmapped sheets (no relationship between the rows in each sheet).
  const handleExportTutorsAndStudents = () => {
    const tutorNames = tutors
      .map((t) => t.profiles?.full_name)
      .filter(Boolean)
      .sort((a, b) => a.localeCompare(b));

    const studentNames = students
      .map((s) => s.full_name)
      .filter(Boolean)
      .sort((a, b) => a.localeCompare(b));

    const workbook = XLSX.utils.book_new();

    const tutorsSheet = XLSX.utils.aoa_to_sheet([
      ["TutorName"],
      ...tutorNames.map((name) => [name]),
    ]);
    tutorsSheet["!cols"] = [{ wch: 30 }];
    XLSX.utils.book_append_sheet(workbook, tutorsSheet, "Tutors");

    const studentsSheet = XLSX.utils.aoa_to_sheet([
      ["StudentName"],
      ...studentNames.map((name) => [name]),
    ]);
    studentsSheet["!cols"] = [{ wch: 30 }];
    XLSX.utils.book_append_sheet(workbook, studentsSheet, "Students");

    XLSX.writeFile(workbook, "Tutors_and_Students.xlsx");
  };


  // Collapses stray whitespace (e.g. accidental leading/trailing/double spaces
  // typed into a name field) and normalizes accents/character width so that
  // "Ayla Smyth" and "Ayla  Smyth " are recognized as the same person.
  const normalizeName = (value) =>
    String(value ?? "")
      .normalize("NFKC")
      .replace(/\s+/g, " ")
      .trim()
      .toLowerCase();

  // Strips a trailing nickname in parentheses, e.g. "Konstantino Gerassis (Kosta)"
  // -> "Konstantino Gerassis", so nicknames added in the sheet don't block a match.
  const stripTrailingNickname = (value) =>
    String(value ?? "").replace(/\s*\([^)]*\)\s*$/, "").trim();

  // Comparison-only form of a name. Punctuation carries no meaning in a person's
  // name — "Dr Tutor" and "Dr. Tutor" are the same tutor, and no one should have
  // to get a full stop right in a spreadsheet — so it is removed before names are
  // compared. Apostrophes vanish ("O'Connor" -> "oconnor") while separators like
  // . - _ / become spaces, since those stand in for a gap between words.
  //
  // Only ever used to compare. Names are stored and displayed exactly as typed.
  const matchKey = (value) =>
    normalizeName(value)
      .replace(/['‘’`]/g, "")
      .replace(/[.,;:!?\-_/\\|]+/g, " ")
      .replace(/\s+/g, " ")
      .trim();

  const getRowValue = (row, possibleHeaders) => {
    const normalizedRow = Object.entries(row).reduce((acc, [key, value]) => {
      acc[String(key).trim().toLowerCase()] = value;
      return acc;
    }, {});

    for (const header of possibleHeaders) {
      const value = normalizedRow[String(header).trim().toLowerCase()];
      if (value !== undefined && value !== null && String(value).trim() !== "") {
        return value;
      }
    }

    return "";
  };

  // Reads the uploaded sheet and works out exactly what would change, without
  // writing anything. The result is held in `pendingUpload` and rendered as a
  // review list; the database is only touched once an admin approves it.
  const handleBulkHoursUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setUploadingHours(true);
    // Drop any batch already on screen straight away, so a failed or empty
    // second upload can never leave the previous file's Approve button live.
    setPendingUpload(null);

    try {
      const buffer = await file.arrayBuffer();
      const workbook = XLSX.read(buffer, { type: "array" });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(sheet, { defval: "" });

      if (!rows.length) {
        setBulkUploadDialog({
          open: true,
          title: "Nothing to review",
          message: "The uploaded spreadsheet is empty.",
          severity: "warning",
          rows: [],
        });
        return;
      }

      const plannedRows = [];

      // Tracks the running hours_bought total per target across this upload.
      // If the sheet has repeated Student/Tutor rows (the same pairing
      // appearing more than once), each row must stack on top of the previous
      // row's addition — not both compute off the same pre-upload snapshot,
      // which would cause the last row to overwrite the earlier one's hours
      // instead of adding to them. New pairings are keyed by student+tutor
      // because they have no assignment id yet.
      const runningHoursByTarget = new Map();

      // Students this sheet is going to create, keyed by matchKey. Later rows
      // are matched against this as well as against the database, otherwise a
      // sheet that introduces "Standard Student" on one row and misspells it
      // "Standard Studnt" on another would create two separate people — the
      // typo has nothing to match against, because the real student doesn't
      // exist yet. The first spelling of a name wins and every later row
      // reuses it.
      const plannedNewStudents = new Map();

      const addErrorRow = ({
        rowNumber,
        studentName = "",
        tutorName = "",
        message,
      }) => {
        plannedRows.push({
          rowNumber,
          studentName,
          tutorName,
          changeType: "error",
          message,
        });
      };

      for (const [index, row] of rows.entries()) {
        const rowNumber = index + 2;
        const studentName = String(
          getRowValue(row, ["Student", "Student Name"]),
        ).trim();
        const tutorName = String(
          getRowValue(row, ["Tutor", "Tutor Name"]),
        ).trim();
        const newHours = Number(
          getRowValue(row, [
            "Number of New Hours",
            "New Hours",
            "Hours",
            "Hours Bought",
          ]),
        );
        const rate = Number(
          getRowValue(row, ["Price Per Hour", "Rate", "Hourly Rate"]),
        );

        if (!studentName || !tutorName) {
          addErrorRow({
            rowNumber,
            message: "Student or Tutor is blank.",
          });
          continue;
        }

        if (!isValidUploadHours(newHours)) {
          addErrorRow({
            rowNumber,
            studentName,
            tutorName,
            message:
              "Hours must be a number, and cannot be 0. Use a negative number to move hours off a tutor.",
          });
          continue;
        }

        const isRemoval = newHours < 0;

        // A removal only moves hours; it never reprices the package, so the
        // rate cell is optional on those rows. A rate that IS filled in still
        // has to be sensible, otherwise a typo would slip through unnoticed.
        const rateCell = String(
          getRowValue(row, ["Price Per Hour", "Rate", "Hourly Rate"]),
        ).trim();
        const rateOmitted = isRemoval && rateCell === "";

        const studentMatchKey = matchKey(studentName);
        const nicknameStrippedMatchKey = matchKey(
          stripTrailingNickname(studentName),
        );

        let student = students.find(
          (s) => matchKey(s.full_name) === studentMatchKey,
        );

        if (!student && nicknameStrippedMatchKey !== studentMatchKey) {
          student = students.find(
            (s) => matchKey(s.full_name) === nicknameStrippedMatchKey,
          );
        }

        let studentMatchNote = "";
        // Set when the student is not on file yet and will be created on
        // approval. The stored name is cleaned the same way the Add New
        // Student form cleans it, so the two routes can't create names that
        // differ only by stray spacing.
        let newStudentName = null;
        let similarStudentWarning = "";

        if (!student) {
          const closest = findClosestStudent(studentName, students, matchKey);
          const percent = closest ? Math.round(closest.similarity * 100) : 0;

          if (closest && closest.similarity >= 0.9) {
            student = closest.student;
            studentMatchNote = `Read as "${student.full_name}" (${percent}% match).`;
          } else {
            const cleanedName = studentName.trim().replace(/\s+/g, " ");
            const cleanedKey = matchKey(cleanedName);

            // Match against the students this sheet is already going to
            // create, not just the ones on file. Same two-step as above:
            // exact on matchKey first, then the 90% typo allowance.
            let planned = plannedNewStudents.get(cleanedKey);
            let plannedScore = 1;

            if (!planned) {
              let best = null;
              for (const candidate of plannedNewStudents.values()) {
                const score = calculateSimilarity(cleanedKey, candidate.key);
                if (!best || score > best.score) best = { score, candidate };
              }
              if (best && best.score >= 0.9) {
                planned = best.candidate;
                plannedScore = best.score;
              }
            }

            if (planned) {
              // Reuse the first spelling, so the batch creates one student.
              newStudentName = planned.name;
              studentMatchNote =
                plannedScore === 1
                  ? `New student, also on row ${planned.firstRow}.`
                  : `Read as "${planned.name}" (${Math.round(
                      plannedScore * 100,
                    )}% match), the new student from row ${planned.firstRow}.`;
            } else {
              newStudentName = cleanedName;
              plannedNewStudents.set(cleanedKey, {
                key: cleanedKey,
                name: cleanedName,
                firstRow: rowNumber,
              });

              // Close to another name, but not close enough to assume it's the
              // same person. Creating the student is still allowed — the
              // reviewer decides, and the score is what they decide on. The
              // near-miss is worth flagging whether it's someone already on
              // file or someone else this same sheet is about to create.
              let nearMiss = closest
                ? { name: closest.student.full_name, score: closest.similarity }
                : null;

              for (const candidate of plannedNewStudents.values()) {
                if (candidate.key === cleanedKey) continue;
                const score = calculateSimilarity(cleanedKey, candidate.key);
                if (score >= 0.6 && (!nearMiss || score > nearMiss.score)) {
                  nearMiss = {
                    name: candidate.name,
                    score,
                    fromRow: candidate.firstRow,
                  };
                }
              }

              if (nearMiss) {
                similarStudentWarning = `Check this: ${Math.round(
                  nearMiss.score * 100,
                )}% match to "${nearMiss.name}"${
                  nearMiss.fromRow
                    ? `, the new student from row ${nearMiss.fromRow}`
                    : ""
                }.`;
              }
            }
          }
        }

        const tutorMatchKey = matchKey(tutorName);

        let tutor = tutors.find(
          (t) => matchKey(t.profiles?.full_name) === tutorMatchKey,
        );

        let tutorMatchNote = "";

        if (!tutor) {
          let closestTutor = null;
          let closestTutorName = null;
          let bestScore = 0;
          for (const t of tutors) {
            const name = t.profiles?.full_name;
            if (!name) continue;
            const score = calculateSimilarity(tutorMatchKey, matchKey(name));
            if (score > bestScore) {
              bestScore = score;
              closestTutor = t;
              closestTutorName = name;
            }
          }
          if (closestTutor && bestScore >= 0.9) {
            tutor = closestTutor;
            tutorMatchNote = `Tutor read as "${closestTutorName}" (${Math.round(
              bestScore * 100,
            )}% match).`;
          } else {
            // Tutors can't be created from a sheet — they come from an
            // approved sign-up — so an unknown tutor is still a hard error.
            const hint =
              closestTutorName && bestScore >= 0.6
                ? ` Did you mean "${closestTutorName}"?`
                : "";
            addErrorRow({
              rowNumber,
              studentName,
              tutorName,
              message: `Tutor "${tutorName}" is not in the system.${hint}`,
            });
            continue;
          }
        }

        // Checked once the tutor is known, because the ceiling is per tutor:
        // R240 for most, higher for a tutor with an approved exception.
        const maxRate = maxRateFor(tutor);

        if (!rateOmitted && !isValidHourlyRate(rate, maxRate)) {
          addErrorRow({
            rowNumber,
            studentName,
            tutorName,
            message: isRemoval
              ? `Rate must be R130 to R${maxRate}, in tens — or left blank, since removing hours does not change the rate.`
              : `Rate must be R130 to R${maxRate}, in tens. For example 150.`,
          });
          continue;
        }

        // Neither a missing student nor a missing Student/Tutor pairing is a
        // failure any more. Both are planned and flagged so the reviewer can
        // see exactly what will be created before approving.
        //
        // A student being created is identified by its normalized name until
        // it has a real id, so several rows naming the same new student (with
        // different tutors, or with different spacing) all point at one
        // student that gets created once.
        const studentKey = student
          ? `student:${student.id}`
          : `newstudent:${matchKey(newStudentName)}`;

        const existingAssignment = student
          ? student.assignments?.find((a) => a.tutor_id === tutor.id)
          : null;

        // There is nothing to take hours away from until the pairing exists.
        if (isRemoval && !existingAssignment) {
          addErrorRow({
            rowNumber,
            studentName,
            tutorName,
            message: `${
              student ? student.full_name : studentName
            } is not linked to ${
              tutor.profiles?.full_name || tutorName
            } yet, so there are no hours to remove.`,
          });
          continue;
        }

        const targetKey = existingAssignment
          ? `assignment:${existingAssignment.id}`
          : `pair:${studentKey}:${tutor.id}`;

        const hoursBefore = runningHoursByTarget.has(targetKey)
          ? runningHoursByTarget.get(targetKey)
          : Number(existingAssignment?.hours_bought || 0);
        // Round to 2dp so repeated additions of fractional hours can't drift
        // into values like 10.299999999999999.
        const hoursAfter = Math.round((hoursBefore + newHours) * 100) / 100;
        runningHoursByTarget.set(targetKey, hoursAfter);

        plannedRows.push({
          rowNumber,
          studentName,
          tutorName,
          matchedStudentName: student ? student.full_name : newStudentName,
          matchedTutorName: tutor.profiles?.full_name || tutorName,
          changeType: existingAssignment
            ? "update"
            : student
              ? "new-pairing"
              : "new-student",
          targetKey,
          studentKey,
          assignmentId: existingAssignment?.id || null,
          studentId: student ? student.id : null,
          newStudentName,
          tutorId: tutor.id,
          newHours,
          isRemoval,
          // A removal never writes a rate, so it has none to show or to
          // conflict with.
          rate: isRemoval ? null : rate,
          rateBefore:
            existingAssignment && !isRemoval
              ? Number(existingAssignment.rate || 0)
              : null,
          // Hours already logged against this pairing (approved + pending).
          // The floor a removal cannot take the package below.
          usedHours: Number(existingAssignment?.usedHours || 0),
          // What the pairing holds before this sheet touches it. Needed to
          // recompute a total from scratch once some rows have been rejected.
          startingHours: Number(existingAssignment?.hours_bought || 0),
          hoursBefore,
          hoursAfter,
          matchNote: [studentMatchNote, tutorMatchNote, similarStudentWarning]
            .filter(Boolean)
            .join(" "),
          hasWarning: Boolean(similarStudentWarning),
        });
      }

      // Hours from repeated rows stack, but a rate is a single value on the
      // assignment — so a Student/Tutor pair listed more than once has to
      // agree with itself. Two different rates for one pair means the sheet
      // is contradicting itself, and quietly taking the last one is exactly
      // how a wrong rate reaches payroll. Flag every row of the pair, since
      // only the person who made the sheet knows which rate is right.
      // Removals are skipped here — they carry no rate to disagree about.
      // The row numbers are collected in this first pass, before anything is
      // marked as an error. Recomputing them inside the second pass would
      // shrink the list as that same pass flags rows, so the last row of a
      // conflict would report only itself.
      const ratesByTarget = new Map();

      for (const row of plannedRows) {
        if (row.changeType === "error" || row.isRemoval) continue;
        const seen = ratesByTarget.get(row.targetKey) || {
          rates: new Set(),
          rowNumbers: [],
        };
        seen.rates.add(row.rate);
        seen.rowNumbers.push(row.rowNumber);
        ratesByTarget.set(row.targetKey, seen);
      }

      for (const row of plannedRows) {
        if (row.changeType === "error" || row.isRemoval) continue;

        const target = ratesByTarget.get(row.targetKey);
        if (!target || target.rates.size < 2) continue;

        const rates = target.rates;
        const conflictingRows = target.rowNumbers;

        row.changeType = "error";
        row.hasWarning = false;
        row.message = `${row.matchedStudentName} and ${
          row.matchedTutorName
        } appear on rows ${conflictingRows.join(", ")} with different rates (${[
          ...rates,
        ]
          .sort((a, b) => a - b)
          .map((r) => `R${r}`)
          .join(
            ", ",
          )}). Every row for the same student and tutor must use the same rate.`;
      }

      // Hours a tutor has already logged cannot be taken back out from under
      // them — those lessons happened, and payroll is calculated from them. So
      // a pairing may be reduced down to, but never below, its used hours.
      // Checked on the pair's FINAL total rather than row by row, so a sheet
      // that removes 8 and adds 6 back is judged on where it lands, not on the
      // dip in the middle.
      //
      // The total is recomputed here from the rows that are still standing,
      // NOT from `runningHoursByTarget` — that was accumulated during the
      // parse loop, before the rate check above rejected anything, and the
      // approve step skips error rows. Reusing it would let a rejected row's
      // hours prop up the total and hide a floor breach the reviewer is going
      // to hit the moment they fix the rates.
      const survivingByTarget = new Map();

      for (const row of plannedRows) {
        if (row.changeType === "error") continue;
        const seen = survivingByTarget.get(row.targetKey);
        survivingByTarget.set(row.targetKey, {
          rows: [...(seen?.rows || []), row],
          delta:
            Math.round(((seen?.delta || 0) + row.newHours) * 100) / 100,
        });
      }

      for (const target of survivingByTarget.values()) {
        const sample = target.rows[0];
        const finalHours =
          Math.round((sample.startingHours + target.delta) * 100) / 100;

        // Tolerance so 4.0000000001 vs 4 can't fail on float noise alone.
        if (finalHours >= sample.usedHours - 0.000001) continue;

        const affectedRows = target.rows.map((r) => r.rowNumber);

        const message =
          sample.usedHours > 0
            ? `${sample.matchedStudentName} and ${sample.matchedTutorName} would drop to ${finalHours} hrs, but ${sample.usedHours} hrs have already been logged. The lowest you can go is ${sample.usedHours}. Rows ${affectedRows.join(
                ", ",
              )}.`
            : `${sample.matchedStudentName} and ${sample.matchedTutorName} would drop to ${finalHours} hrs. Hours cannot go below 0. Rows ${affectedRows.join(
                ", ",
              )}.`;

        // Only the surviving rows are touched — a row already rejected for a
        // rate conflict keeps its own message, so the reviewer sees both
        // problems in one pass instead of discovering the second one after
        // fixing the first.
        for (const affected of target.rows) {
          affected.changeType = "error";
          affected.hasWarning = false;
          affected.message = message;
        }
      }

      setPendingUpload({ fileName: file.name, rows: plannedRows });
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (error) {
      console.error("Bulk hours upload failed:", error);
      setBulkUploadDialog({
        open: true,
        title: "Bulk upload failed",
        message: error.message,
        severity: "error",
        rows: [],
      });
    } finally {
      setUploadingHours(false);
      event.target.value = "";
    }
  };

  // Counted per Student/Tutor pair rather than per sheet row, so a pairing
  // listed twice in the file reads as one change (which is what actually
  // gets written) instead of two.
  const pendingUploadSummary = (() => {
    const rows = pendingUpload?.rows || [];
    const keysFor = (changeType) =>
      new Set(
        rows.filter((r) => r.changeType === changeType).map((r) => r.targetKey),
      ).size;

    const newPairings = keysFor("new-pairing");
    const updates = keysFor("update");
    const newStudentLinks = keysFor("new-student");

    return {
      newPairings,
      updates,
      newStudentLinks,
      // Distinct students that don't exist yet — one row per student, however
      // many tutors the sheet links them to.
      newStudents: new Set(
        rows
          .filter((r) => r.changeType === "new-student")
          .map((r) => r.studentKey),
      ).size,
      changes: newPairings + updates + newStudentLinks,
      errors: rows.filter((r) => r.changeType === "error").length,
      // Counted per student, not per row — one new student listed against
      // three tutors is still one name to double-check.
      warnings: new Set(
        rows.filter((r) => r.hasWarning).map((r) => r.studentKey),
      ).size,
      total: rows.length,
    };
  })();

  const handleDenyPendingUpload = () => {
    setPendingUpload(null);
  };

  // Writes the approved batch. Rows are collapsed to one write per
  // Student/Tutor pair, and hours are applied as a delta against a freshly
  // read hours_bought rather than the absolute total worked out at parse
  // time — the batch can sit on screen for a while, and someone else may
  // have adjusted the same package (or created the pairing) in the meantime.
  const handleApprovePendingUpload = async () => {
    if (!pendingUpload || pendingUploadSummary.errors > 0) return;

    setApplyingUpload(true);

    try {
      const targets = new Map();

      for (const row of pendingUpload.rows) {
        if (row.changeType === "error") continue;
        const existing = targets.get(row.targetKey);
        targets.set(row.targetKey, {
          targetKey: row.targetKey,
          studentKey: row.studentKey,
          studentId: row.studentId,
          newStudentName: row.newStudentName,
          tutorId: row.tutorId,
          studentLabel: row.matchedStudentName,
          tutorLabel: row.matchedTutorName,
          // Later rows win on rate, matching a top-to-bottom read of the sheet
          // — but only rows that ADD hours carry one. A pairing that is only
          // reduced keeps the rate it already has, so moving hours off a tutor
          // can never reprice the work they already did. `null` here means
          // "leave the rate alone".
          rate: row.isRemoval ? (existing?.rate ?? null) : row.rate,
          hoursToAdd:
            Math.round(((existing?.hoursToAdd || 0) + row.newHours) * 100) / 100,
          rowNumbers: [...(existing?.rowNumbers || []), row.rowNumber],
        });
      }

      const targetList = [...targets.values()];

      const results = [];
      const failedTargetKeys = new Set();
      let successCount = 0;
      let failedCount = 0;

      // ---- 1. Create the students that don't exist yet ------------------
      // One student per distinct name, however many tutors reference it.
      // Every row for a given new student already carries the same
      // `newStudentName` (the first spelling the sheet used), so which row
      // wins this de-duplication doesn't change what gets stored.
      // Every target for a student that can't be created is failed rather
      // than silently skipped, so the reviewer sees why nothing happened.
      const newStudentNames = [
        ...new Map(
          targetList
            .filter((t) => !t.studentId)
            .map((t) => [t.studentKey, t.newStudentName]),
        ),
      ];

      const studentIdByKey = new Map();
      const createdStudentKeys = new Set();

      if (newStudentNames.length > 0) {
        // Someone may have added one of these names since the file was read.
        // Reuse that row instead of tripping the unique constraint on
        // students.full_name.
        const { data: alreadyThere, error: lookupError } = await supabase
          .from("students")
          .select("id, full_name")
          .in(
            "full_name",
            newStudentNames.map(([, name]) => name),
          );

        if (lookupError) throw lookupError;

        const existingByName = new Map(
          (alreadyThere || []).map((s) => [matchKey(s.full_name), s.id]),
        );

        for (const [key, name] of newStudentNames) {
          const alreadyId = existingByName.get(matchKey(name));
          if (alreadyId) {
            studentIdByKey.set(key, alreadyId);
            continue;
          }

          let { data: created, error: createError } = await supabase
            .from("students")
            .insert([{ full_name: name }])
            .select("id")
            .single();

          // 23505 means a student with exactly this name appeared between the
          // lookup above and this insert. Re-read it and use it rather than
          // failing the batch — the admin's intent was "this student", not
          // "a brand new row".
          if (createError?.code === "23505") {
            const retry = await supabase
              .from("students")
              .select("id")
              .eq("full_name", name)
              .maybeSingle();

            if (retry.data) {
              studentIdByKey.set(key, retry.data.id);
              continue;
            }
          }

          if (createError || !created) {
            for (const target of targetList.filter(
              (t) => t.studentKey === key,
            )) {
              failedCount += 1;
              failedTargetKeys.add(target.targetKey);
              results.push({
                rowNumber: target.rowNumbers.join(", "),
                studentName: target.studentLabel,
                tutorName: target.tutorLabel,
                status: "Error",
                message: `Could not create student "${name}": ${
                  createError?.message || "no record was returned."
                }`,
              });
            }
            continue;
          }

          studentIdByKey.set(key, created.id);
          createdStudentKeys.add(key);
        }
      }

      // Targets whose student could not be created are dropped here; the
      // rest now all have a real student id to write against.
      const writableTargets = targetList
        .filter((t) => !failedTargetKeys.has(t.targetKey))
        .map((t) => ({
          ...t,
          studentId: t.studentId || studentIdByKey.get(t.studentKey),
        }))
        .filter((t) => t.studentId);

      // ---- 2. Apply the hours -------------------------------------------
      // Skipped entirely when every student creation failed, so the results
      // gathered above still reach the dialog instead of being thrown away by
      // an empty `.in()` query erroring out.
      const freshByPair = new Map();
      const freshUsedByPair = new Map();

      if (writableTargets.length > 0) {
        const { data: freshAssignments, error: refreshError } = await supabase
          .from("student_tutor_assignments")
          .select("id, tutor_id, student_id, hours_bought")
          .in("student_id", [
            ...new Set(writableTargets.map((t) => t.studentId)),
          ]);

        if (refreshError) throw refreshError;

        for (const a of freshAssignments || []) {
          freshByPair.set(`${a.student_id}:${a.tutor_id}`, a);
        }

        // Lessons may have been logged while the batch sat on screen, so the
        // floor is re-read here rather than trusted from the page-load
        // snapshot. Without this a removal approved ten minutes late could
        // still strip hours a tutor has since worked.
        const { data: freshLogs, error: logsError } = await supabase
          .from("lesson_logs")
          .select("student_id, tutor_id, hours, status")
          .in("student_id", [
            ...new Set(writableTargets.map((t) => t.studentId)),
          ])
          .in("status", ["approved", "pending"]);

        if (logsError) throw logsError;

        for (const log of freshLogs || []) {
          const key = `${log.student_id}:${log.tutor_id}`;
          freshUsedByPair.set(
            key,
            Math.round(
              ((freshUsedByPair.get(key) || 0) + Number(log.hours || 0)) * 100,
            ) / 100,
          );
        }
      }

      for (const target of writableTargets) {
        const label = target.rowNumbers.join(", ");
        const current = freshByPair.get(
          `${target.studentId}:${target.tutorId}`,
        );

        let error = null;
        let written = null;

        // The insert/update branch is decided by what is in the database NOW,
        // not by what the parse step saw. If a pairing was deleted between
        // upload and approval, a removal-only target would otherwise fall into
        // the insert branch and create a fresh assignment with negative hours
        // and no rate. Refuse instead.
        if (!current && (target.rate === null || target.hoursToAdd < 0)) {
          failedCount += 1;
          failedTargetKeys.add(target.targetKey);
          results.push({
            rowNumber: label,
            studentName: target.studentLabel,
            tutorName: target.tutorLabel,
            status: "Error",
            message:
              "This pairing no longer exists, so there are no hours to remove from it. Nothing was saved.",
          });
          continue;
        }

        if (current) {
          const hoursAfter =
            Math.round(
              (Number(current.hours_bought || 0) + target.hoursToAdd) * 100,
            ) / 100;

          // Floor re-check against freshly read logged hours.
          const usedNow =
            freshUsedByPair.get(`${target.studentId}:${target.tutorId}`) || 0;

          if (hoursAfter < usedNow - 0.000001) {
            failedCount += 1;
            failedTargetKeys.add(target.targetKey);
            results.push({
              rowNumber: label,
              studentName: target.studentLabel,
              tutorName: target.tutorLabel,
              status: "Error",
              message: `Would drop to ${hoursAfter} hrs, but ${usedNow} hrs are now logged against this pairing. Nothing was saved.`,
            });
            continue;
          }

          // A rate of null means every row for this pair was a removal, so
          // the column is left out of the update entirely rather than written
          // back as null.
          const response = await supabase
            .from("student_tutor_assignments")
            .update(
              target.rate === null
                ? { hours_bought: hoursAfter }
                : { hours_bought: hoursAfter, rate: target.rate },
            )
            .eq("id", current.id)
            .select("id, hours_bought");
          error = response.error;
          written = response.data?.[0] || null;
        } else {
          const response = await supabase
            .from("student_tutor_assignments")
            .insert([
              {
                tutor_id: target.tutorId,
                student_id: target.studentId,
                rate: target.rate,
                hours_bought: target.hoursToAdd,
              },
            ])
            .select("id, hours_bought");
          error = response.error;
          written = response.data?.[0] || null;
        }

        // A write that matches no row comes back without an error and
        // without data — the record was deleted, or RLS silently filtered
        // it out (coordinators have read-only access here). Treat it as a
        // failure rather than reporting a save that never happened.
        if (!error && !written) {
          error = {
            message:
              "Nothing was saved — the record may have been deleted, or your role may not have permission to change it.",
          };
        }

        if (error) {
          failedCount += 1;
          failedTargetKeys.add(target.targetKey);
          results.push({
            rowNumber: label,
            studentName: target.studentLabel,
            tutorName: target.tutorLabel,
            status: "Error",
            message:
              error.code === "23505"
                ? "This pairing was created by someone else moments ago. Approve again to add the hours to it."
                : `Not saved: ${error.message}`,
          });
        } else {
          successCount += 1;
          results.push({
            rowNumber: label,
            studentName: target.studentLabel,
            tutorName: target.tutorLabel,
            status: current ? "Updated" : "Created",
            message: current
              ? `${
                  target.hoursToAdd < 0
                    ? `Removed ${Math.abs(target.hoursToAdd)} hrs`
                    : `Added ${target.hoursToAdd} hrs`
                }. Now ${written.hours_bought} hrs${
                  target.rate === null
                    ? ", rate unchanged."
                    : ` at R${target.rate}/hr.`
                }`
              : `${
                  createdStudentKeys.has(target.studentKey)
                    ? "Student created and linked"
                    : "Linked"
                } with ${written.hours_bought} hrs at R${target.rate}/hr.`,
          });
        }
      }

      await fetchData();

      // Anything that saved is dropped from the batch, so approving again
      // can never add the same hours twice. Only failures stay on screen.
      if (failedTargetKeys.size > 0) {
        setPendingUpload((prev) =>
          prev
            ? {
                ...prev,
                rows: prev.rows.filter((r) =>
                  failedTargetKeys.has(r.targetKey),
                ),
              }
            : prev,
        );
      } else {
        setPendingUpload(null);
      }

      setBulkUploadDialog({
        open: true,
        title:
          failedCount > 0
            ? "Some changes could not be applied"
            : "Changes applied",
        message:
          failedCount > 0
            ? `Applied: ${successCount}. Failed: ${failedCount}. The successful changes have been saved and removed from the review list — only the failed ones are still listed, so you can safely approve again.`
            : `Applied: ${successCount}.`,
        severity: failedCount > 0 ? "warning" : "success",
        rows: results,
      });
    } catch (error) {
      console.error("Applying bulk upload failed:", error);
      setBulkUploadDialog({
        open: true,
        title: "Could not apply changes",
        message: error.message,
        severity: "error",
        rows: [],
      });
    } finally {
      setApplyingUpload(false);
    }
  };

  // -------------------- Search & Pagination Logic -------------------- //

  const filteredStudents = students.filter((s) =>
    s.full_name.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  const paginatedStudents = filteredStudents.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage,
  );

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    window.scrollTo({ top: 0, behavior: "instant" });
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value));
    setPage(0);
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    setPage(0);
  };

  // -------------------- Handlers for Student Management -------------------- //

  const handleCreateStudent = async (e) => {
    e.preventDefault();

    // Collapse stray leading/trailing/double spaces before saving, so the
    // stored name always matches what gets typed into a bulk upload sheet.
    const cleanedStudentName = studentName.trim().replace(/\s+/g, " ");

    // 1. Similarity Check
    const similarMatch = findSimilarStudent(cleanedStudentName, students);
    if (similarMatch) {
      const proceed = window.confirm(
        `Possible duplicate detected!\n\nA student named "${similarMatch.student.full_name}" already exists.\nAre you sure you want to create "${cleanedStudentName}"?`,
      );
      if (!proceed) return;
    }

    const { error } = await supabase
      .from("students")
      .insert([{ full_name: cleanedStudentName }]);

    if (!error) {
      setStudentName("");
      fetchData();
      alert("Student created successfully!");
    } else {
      alert("Error creating student: " + error.message);
    }
  };

  const handleStudentNameUpdate = async () => {
    // Collapse stray leading/trailing/double spaces before saving, so the
    // stored name always matches what gets typed into a bulk upload sheet.
    const cleanedName = newStudentNameValue.trim().replace(/\s+/g, " ");

    if (!cleanedName) return;

    // Similarity check against every OTHER student (full_name is UNIQUE in
    // the DB, so warn before a likely duplicate/typo gets saved).
    const otherStudents = students.filter((s) => s.id !== selectedStudent);
    const similarMatch = findSimilarStudent(cleanedName, otherStudents);
    if (similarMatch) {
      const proceed = window.confirm(
        `Possible duplicate detected!\n\nA student named "${similarMatch.student.full_name}" already exists.\nAre you sure you want to rename this student to "${cleanedName}"?`,
      );
      if (!proceed) return;
    }

    const { error } = await supabase
      .from("students")
      .update({ full_name: cleanedName })
      .eq("id", selectedStudent);

    if (error) {
      if (error.code === "23505") {
        alert(`Error updating name: a student named "${cleanedName}" already exists.`);
      } else {
        alert("Error updating name: " + error.message);
      }
    } else {
      setOpenStudentNameDialog(false);
      setSelectedStudent(null);
      setNewStudentNameValue("");
      fetchData();
    }
  };

  const handleDeleteStudent = async (id) => {
    if (window.confirm("Are you sure you want to delete this student?")) {
      if (window.confirm("This action cannot be undone.")) {
        setLoading(true);
        const { error } = await supabase.from("students").delete().eq("id", id);
        if (!error) fetchData();
        setLoading(false);
      }
    }
  };

  const handleAssignTutor = async (e) => {
    e.preventDefault();

    const rate = Number(assignment.rate);
    const hoursBought = Number(assignment.hoursBought);

    if (!isValidHourlyRate(rate, assignMaxRate)) {
      alert(`Hourly Rate: ${hourlyRateRule(assignMaxRate)}`);
      return;
    }

    if (!isValidHoursAmount(hoursBought)) {
      alert("Initial Hours must be greater than 0.");
      return;
    }

    setLoading(true);
    const { error } = await supabase.from("student_tutor_assignments").insert([
      {
        tutor_id: assignment.tutorId,
        student_id: assignment.studentId,
        rate,
        hours_bought: hoursBought,
      },
    ]);

    if (!error) {
      setAssignment({ studentId: "", tutorId: "", rate: "", hoursBought: "" });
      fetchData();
      alert("Successfully assigned student and package.");
    } else {
      alert(
        "Error: Assignment between the selected tutor and student already exists.",
      );
    }
    setLoading(false);
  };

  const handleToggleExceed = async (studentId, newValue) => {
    setStudents((prev) =>
      prev.map((s) =>
        s.id === studentId ? { ...s, can_exceed: newValue } : s,
      ),
    );

    const { error } = await supabase
      .from("students")
      .update({ can_exceed: newValue })
      .eq("id", studentId);

    if (error) {
      console.error("Error updating exceed status:", error.message);
      fetchData();
    }
  };

  const handleUpdateCredits = async () => {
    const rawAdjustment = Number(adjustmentValue);
    const roundedAdjustment = Math.round(rawAdjustment * 4) / 4;
    const newTotal =
      Number(selectedAssignment.hours_bought || 0) + roundedAdjustment;

    const { error } = await supabase
      .from("student_tutor_assignments")
      .update({ hours_bought: newTotal })
      .eq("id", selectedAssignment.id);

    if (!error) {
      setOpenCreditDialog(false);
      setAdjustmentValue(0);
      fetchData();
    }
  };

  // -------------------- Render -------------------- //

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 4 }}
      >
        <Typography
          variant="h4"
          sx={{ fontWeight: "bold", color: "primary.main" }}
        >
          Student Management
        </Typography>

        <Stack direction="row" spacing={1.5} alignItems="center">
          <Button
            variant="outlined"
            size="small"
            onClick={handleDownloadBulkHoursTemplate}
            startIcon={<DownloadIcon />}
            sx={{
              height: 40,
              px: 2,
              fontSize: "0.8rem",
              fontWeight: 700,
              whiteSpace: "nowrap",
            }}
          >
            Template
          </Button>

          <Button
            variant="outlined"
            size="small"
            onClick={handleExportTutorsAndStudents}
            startIcon={<GroupsIcon />}
            sx={{
              height: 40,
              px: 2,
              fontSize: "0.8rem",
              fontWeight: 700,
              whiteSpace: "nowrap",
            }}
          >
            Tutors &amp; Students
          </Button>

          {!readOnly && (
            <Button
              variant="contained"
              size="small"
              component="label"
              disabled={uploadingHours || applyingUpload}
              sx={{
                height: 40,
                px: 2,
                fontSize: "0.8rem",
                fontWeight: 700,
                whiteSpace: "nowrap",
              }}
            >
              {uploadingHours
                ? "Checking..."
                : pendingUpload
                  ? "Upload Different File"
                  : "Upload Hours Excel"}
              <input
                hidden
                type="file"
                accept=".xlsx,.xls,.csv"
                onChange={handleBulkHoursUpload}
              />
            </Button>
          )}

          {/* Search Bar Integration */}
          <TextField
            size="small"
            placeholder="Search student name..."
            value={searchQuery}
            onChange={handleSearchChange}
            sx={{ width: 300, bgcolor: "white" }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              },
            }}
          />
        </Stack>
      </Stack>

      {/* Pending bulk upload review — nothing here has been saved yet */}
      {pendingUpload && (
        <Paper
          elevation={0}
          sx={{
            mb: 4,
            border: "2px solid",
            borderColor:
              pendingUploadSummary.errors > 0 ? "error.main" : "primary.main",
            borderRadius: 2,
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              px: 3,
              py: 2,
              bgcolor:
                pendingUploadSummary.errors > 0 ? "error.main" : "primary.main",
              color: "white",
            }}
          >
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              spacing={2}
            >
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                  Review pending changes
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.85 }}>
                  {pendingUpload.fileName} — nothing has been saved yet
                </Typography>
              </Box>
              <Stack direction="row" spacing={1}>
                {[
                  `${pendingUploadSummary.newStudents} new student${
                    pendingUploadSummary.newStudents === 1 ? "" : "s"
                  }`,
                  `${pendingUploadSummary.newPairings} new link${
                    pendingUploadSummary.newPairings === 1 ? "" : "s"
                  }`,
                  `${pendingUploadSummary.updates} hour change${
                    pendingUploadSummary.updates === 1 ? "" : "s"
                  }`,
                  `${pendingUploadSummary.errors} error${
                    pendingUploadSummary.errors === 1 ? "" : "s"
                  }`,
                ].map((label) => (
                  <Chip
                    key={label}
                    size="small"
                    label={label}
                    sx={{ bgcolor: "rgba(255,255,255,0.2)", color: "white" }}
                  />
                ))}
              </Stack>
            </Stack>
          </Box>

          <Box sx={{ p: 3 }}>
            <Alert
              severity={pendingUploadSummary.errors > 0 ? "error" : "info"}
              sx={{ mb: 2 }}
            >
              {pendingUploadSummary.errors > 0
                ? `${pendingUploadSummary.errors} row${
                    pendingUploadSummary.errors === 1 ? "" : "s"
                  } could not be read. Nothing can be approved until the sheet is fixed — correct the rows below and upload the file again.`
                : `${pendingUploadSummary.changes} change${
                    pendingUploadSummary.changes === 1 ? " is" : "s are"
                  } ready. Approve to apply them all, or deny to discard the upload.`}
            </Alert>

            {pendingUploadSummary.errors === 0 &&
              pendingUploadSummary.warnings > 0 && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  {pendingUploadSummary.warnings} new student
                  {pendingUploadSummary.warnings === 1 ? " has" : "s have"} a
                  name close to someone already on file. Check the highlighted
                  rows before approving — approving will create a second
                  student.
                </Alert>
              )}

            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: "bold" }}>Row</TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>Change</TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>Student</TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>Tutor</TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>Hours</TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>Rate</TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>Detail</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pendingUpload.rows.map((row, index) => {
                    const label =
                      CHANGE_LABELS[
                        row.changeType !== "error" && row.isRemoval
                          ? "remove"
                          : row.changeType
                      ];

                    return (
                      <TableRow
                        key={`${row.rowNumber}-${index}`}
                        sx={{ bgcolor: label.tint }}
                      >
                        <TableCell>{row.rowNumber}</TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={label.text}
                            color={label.color}
                          />
                        </TableCell>
                        <TableCell>
                          {row.matchedStudentName || row.studentName || "-"}
                        </TableCell>
                        <TableCell>
                          {row.matchedTutorName || row.tutorName || "-"}
                        </TableCell>
                        <TableCell>
                          {row.changeType === "error"
                            ? "-"
                            : `${row.hoursBefore} → ${row.hoursAfter}`}
                        </TableCell>
                        <TableCell>
                          {row.changeType === "error" || row.rate === null
                            ? "-"
                            : row.rateBefore !== null &&
                                row.rateBefore !== row.rate
                              ? `R${row.rateBefore} → R${row.rate}`
                              : `R${row.rate}`}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {describePendingRow(row)}
                          </Typography>
                          {row.matchNote && (
                            <Typography
                              variant="caption"
                              display="block"
                              color={
                                row.hasWarning ? "warning.main" : "text.secondary"
                              }
                            >
                              {row.matchNote}
                            </Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>

            <Stack
              direction="row"
              spacing={2}
              justifyContent="flex-end"
              sx={{ mt: 3 }}
            >
              <Button
                variant="outlined"
                color="error"
                onClick={handleDenyPendingUpload}
                disabled={applyingUpload}
              >
                Deny &amp; Discard
              </Button>
              <Button
                variant="contained"
                color="primary"
                disableElevation
                onClick={handleApprovePendingUpload}
                disabled={
                  applyingUpload ||
                  pendingUploadSummary.errors > 0 ||
                  pendingUploadSummary.changes === 0
                }
              >
                {applyingUpload
                  ? "Applying..."
                  : `Approve ${pendingUploadSummary.changes} Change${
                      pendingUploadSummary.changes === 1 ? "" : "s"
                    }`}
              </Button>
            </Stack>
          </Box>
        </Paper>
      )}

      <Grid container spacing={4}>
        {/* LEFT SIDE: FORMS */}
        {!readOnly && (
          <Grid size={{ xs: 12, md: 4, lg: 4 }}>
            <Stack spacing={3}>
              <Paper
                elevation={0}
                sx={{ p: 3, border: "1px solid #e0e0e0", borderRadius: 2 }}
              >
                <Typography
                  variant="subtitle1"
                  sx={{ mb: 2, fontWeight: "bold" }}
                >
                  1. Add New Student
                </Typography>
                <Box component="form" onSubmit={handleCreateStudent}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Student Full Name"
                    value={studentName}
                    onChange={(e) => setStudentName(e.target.value)}
                    sx={{ mb: 2 }}
                    required
                  />
                  <Button
                    type="submit"
                    variant="contained"
                    disableElevation
                    fullWidth
                  >
                    Create Student
                  </Button>
                </Box>
              </Paper>

              <Paper
                elevation={0}
                sx={{ p: 3, border: "1px solid #e0e0e0", borderRadius: 2 }}
              >
                <Typography
                  variant="subtitle1"
                  sx={{ mb: 2, fontWeight: "bold" }}
                >
                  2. Link Student to Tutor
                </Typography>
                <Stack component="form" onSubmit={handleAssignTutor} spacing={2}>
                  {/* SEARCHABLE STUDENT SELECT */}
                  <Autocomplete
                    options={students}
                    getOptionLabel={(option) => option.full_name || ""}
                    value={
                      students.find((s) => s.id === assignment.studentId) || null
                    }
                    onChange={(event, newValue) => {
                      setAssignment({
                        ...assignment,
                        studentId: newValue ? newValue.id : "",
                      });
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Select Student"
                        size="small"
                        required
                      />
                    )}
                    slotProps={{
                      listbox: {
                        style: {
                          maxHeight: "200px", // Limits the dropdown height
                        },
                      },
                    }}
                  />

                  {/* SEARCHABLE TUTOR SELECT */}
                  <Autocomplete
                    options={tutors}
                    getOptionLabel={(option) =>
                      option.profiles?.full_name || "Unnamed Tutor"
                    }
                    value={
                      tutors.find((t) => t.id === assignment.tutorId) || null
                    }
                    onChange={(event, newValue) => {
                      setAssignment({
                        ...assignment,
                        tutorId: newValue ? newValue.id : "",
                      });
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Select Tutor"
                        size="small"
                        required
                      />
                    )}
                    slotProps={{
                      listbox: {
                        style: {
                          maxHeight: "200px", // Limits the dropdown height
                        },
                      },
                    }}
                  />

                  <TextField
                    fullWidth
                    size="small"
                    label="Hourly Rate (R)"
                    type="number"
                    helperText={hourlyRateRule(assignMaxRate)}
                    value={assignment.rate}
                    onChange={(e) =>
                      setAssignment({ ...assignment, rate: e.target.value })
                    }
                    onBlur={(e) => {
                      const rate = Number(e.target.value);
                      if (Number.isFinite(rate)) {
                        setAssignment({ ...assignment, rate: Math.round(rate) });
                      }
                    }}
                    slotProps={{
                      htmlInput: {
                        min: 130,
                        max: assignMaxRate,
                        step: 10,
                      },
                    }}
                    required
                  />

                  <TextField
                    fullWidth
                    size="small"
                    label="Initial Hours"
                    type="number"
                    placeholder="e.g. 10"
                    helperText="Must be greater than 0."
                    value={assignment.hoursBought}
                    onChange={(e) =>
                      setAssignment({
                        ...assignment,
                        hoursBought: e.target.value,
                      })
                    }
                    slotProps={{
                      htmlInput: {
                        min: 0.25,
                        step: 0.25,
                      },
                    }}
                    required
                  />

                  <Button
                    type="submit"
                    variant="outlined"
                    fullWidth
                    disabled={
                      loading ||
                      !isValidHourlyRate(assignment.rate, assignMaxRate) ||
                      !isValidHoursAmount(assignment.hoursBought)
                    }
                  >
                    Assign & Set Package
                  </Button>
                </Stack>
              </Paper>
            </Stack>
          </Grid>
        )}

        {/* RIGHT SIDE: LIST */}
        <Grid size={readOnly ? { xs: 12 } : { xs: 12, md: 8, lg: 8 }}>
          {loading && students.length === 0 ? (
            <Box display="flex" justifyContent="center" py={10}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              {paginatedStudents.map((student) => (
                <Accordion
                  key={student.id}
                  variant="outlined"
                  sx={{
                    mb: 2,
                    borderRadius: "12px !important", // Forces all corners to be rounded
                    "&:before": {
                      display: "none", // Removes the default MUI divider line
                    },
                  }}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={3}
                      sx={{ width: "100%" }}
                    >
                      <SchoolIcon color="action" />
                      <Box sx={{ width: "30%" }}>
                        <Stack direction="row" alignItems="center" spacing={0.5}>
                          <Typography variant="subtitle1" fontWeight="bold">
                            {student.full_name}
                          </Typography>
                          {!readOnly && (
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedStudent(student.id);
                                setNewStudentNameValue(student.full_name);
                                setOpenStudentNameDialog(true);
                              }}
                              sx={{ p: 0.5 }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          )}
                        </Stack>
                        <Typography
                          variant="caption"
                          display="block"
                          color={"text.secondary"}
                        >
                          {student.assignments
                            ?.reduce(
                              (acc, asg) =>
                                acc +
                                (Number(asg.hours_bought || 0) -
                                  Number(asg.usedHours || 0)),
                              0,
                            )
                            .toFixed(2)}{" "}
                          hrs remaining
                        </Typography>
                      </Box>

                      <Box
                        sx={{ width: "30%" }}
                        onClick={(e) => e.stopPropagation()} // Prevents accordion from flipping open when toggling
                      >
                        <Typography
                          variant="caption"
                          display="block"
                          color="text.secondary"
                          sx={{ mb: 0.5 }}
                        >
                          BILLING POLICY
                        </Typography>
                        <TextField
                          select
                          size="small"
                          variant="outlined"
                          value={student.can_exceed ? "true" : "false"}
                          disabled={readOnly}
                          onChange={(e) =>
                            handleToggleExceed(
                              student.id,
                              e.target.value === "true",
                            )
                          }
                          slotProps={{
                            select: {
                              native: true,
                              sx: {
                                fontSize: "0.8rem",
                                height: "32px",
                                fontWeight: 600,
                                bgcolor: student.can_exceed
                                  ? "rgba(46, 125, 50, 0.08)"
                                  : "transparent",
                                color: student.can_exceed
                                  ? "success.main"
                                  : "text.primary",
                                "& option": { color: "text.primary" },
                              },
                            },
                          }}
                          sx={{ width: "80%" }}
                        >
                          <option value="false">Strict </option>
                          <option value="true">Flexible </option>
                        </TextField>
                      </Box>
                      <Box sx={{ width: "20%" }}>
                        {/* Show a global warning if ANY assignment is over capacity */}
                        {student.assignments?.some(
                          (asg) => asg.usedHours > asg.hours_bought,
                        ) ? (
                          <Chip label="Over Limit" color="error" size="small" />
                        ) : student.assignments?.every(
                            (asg) => asg.usedHours >= asg.hours_bought,
                          ) ? (
                          <Chip
                            label="Zero Balance"
                            color="warning"
                            size="small"
                          />
                        ) : (
                          <Chip
                            label="Credits OK"
                            color="success"
                            size="small"
                          />
                        )}
                      </Box>

                      <Stack direction="row" spacing={1}>
                        {!readOnly && (
                          <IconButton
                            size="small"
                            color="error"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteStudent(student.id);
                            }}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        )}
                      </Stack>
                    </Stack>
                  </AccordionSummary>
                  <AccordionDetails
                    sx={{
                      borderRadius: "12px !important", // Forces all corners to be rounded
                      "&:before": {
                        display: "none", // Removes the default MUI divider line
                      },
                    }}
                  >
                    <Divider sx={{ mb: 2 }} />
                    <Typography
                      variant="subtitle2"
                      gutterBottom
                      sx={{ fontWeight: "bold", color: "text.secondary" }}
                    >
                      Usage per Tutor
                    </Typography>

                    <Stack spacing={1}>
                      {student.assignments?.length > 0 ? (
                        student.assignments.map((asg) => {
                          const remaining =
                            Number(asg.hours_bought || 0) -
                            Number(asg.usedHours || 0);

                          return (
                            <Paper
                              key={asg.id}
                              elevation={0}
                              sx={{
                                p: 1.5,
                                border: "1px solid #e0e0e0",
                                borderRadius: 1,
                                mb: 1,
                              }}
                            >
                              <Grid container alignItems="center" spacing={2}>
                                <Grid size={{ xs: 5 }}>
                                  <Typography variant="body2" fontWeight="bold">
                                    {asg.tutors?.profiles?.full_name}
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    Rate: R{asg.rate}/hr
                                  </Typography>
                                </Grid>

                                <Grid size={{ xs: 5 }}>
                                  <Typography
                                    variant="body2"
                                    color={
                                      remaining < 0
                                        ? "error.main"
                                        : remaining === 0
                                          ? "warning"
                                          : "text.primary"
                                    }
                                    fontWeight="500"
                                  >
                                    {remaining.toFixed(2)} hrs remaining
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    (Used {asg.usedHours.toFixed(2)} of{" "}
                                    {asg.hours_bought} hrs)
                                  </Typography>
                                </Grid>

                                <Grid
                                  size={{ xs: 2 }}
                                  sx={{ textAlign: "right" }}
                                >
                                  {!readOnly && (
                                    <IconButton
                                      size="small"
                                      onClick={() => {
                                        setSelectedAssignment(asg);
                                        setAdjustmentValue(0);
                                        setOpenCreditDialog(true);
                                      }}
                                    >
                                      <EditIcon fontSize="small" />
                                    </IconButton>
                                  )}
                                </Grid>
                              </Grid>
                            </Paper>
                          );
                        })
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          No active tutor links.
                        </Typography>
                      )}
                    </Stack>
                  </AccordionDetails>
                </Accordion>
              ))}

              {filteredStudents.length === 0 && (
                <Paper sx={{ p: 4, textAlign: "center", bgcolor: "#fafafa" }}>
                  <Typography color="text.secondary">
                    {searchQuery
                      ? `No students found matching "${searchQuery}"`
                      : "No students found."}
                  </Typography>
                </Paper>
              )}

              {filteredStudents.length > 0 && (
                <Paper variant="outlined" sx={{ mt: 2 }}>
                  <TablePagination
                    component="div"
                    count={filteredStudents.length}
                    page={page}
                    onPageChange={handleChangePage}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    rowsPerPageOptions={[5, 10, 25]}
                  />
                </Paper>
              )}
            </>
          )}
        </Grid>
      </Grid>

      {/* Dialog for updating a student's name */}
      <Dialog
        open={openStudentNameDialog}
        onClose={() => setOpenStudentNameDialog(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Update Student Name</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Enter the new display name for this student.
          </Typography>
          <TextField
            autoFocus
            margin="dense"
            label="Student Name"
            type="text"
            fullWidth
            variant="outlined"
            value={newStudentNameValue}
            onChange={(e) => setNewStudentNameValue(e.target.value)}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setOpenStudentNameDialog(false)}
            color="inherit"
          >
            Cancel
          </Button>
          <Button
            onClick={handleStudentNameUpdate}
            variant="contained"
            color="primary"
            disabled={!newStudentNameValue.trim()}
          >
            Update Name
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog for adjusting student credits */}
      <Dialog
        open={openCreditDialog}
        onClose={() => {
          setOpenCreditDialog(false);
          setAdjustmentValue(0);
        }}
      >
        <DialogTitle>
          Adjust Package: {selectedAssignment?.tutors?.profiles?.full_name}
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={3} sx={{ mt: 1, minWidth: 350 }}>
            {/* Contextual Info */}
            <Box
              sx={{
                bgcolor: "primary.main",
                color: "white",
                p: 2,
                borderRadius: 2,
                boxShadow: 1,
              }}
            >
              <Typography
                variant="caption"
                sx={{ opacity: 0.8, display: "block" }}
              >
                STUDENT:{" "}
                {
                  paginatedStudents.find((s) =>
                    s.assignments?.some((a) => a.id === selectedAssignment?.id),
                  )?.full_name
                }
              </Typography>
              <Typography variant="h6">
                {(
                  Number(selectedAssignment?.hours_bought || 0) -
                  Number(selectedAssignment?.usedHours || 0)
                ).toFixed(2)}{" "}
                Hours Remaining
              </Typography>
            </Box>

            <TextField
              label="Add or Subtract Hours"
              type="number"
              fullWidth
              autoFocus
              helperText="e.g., 5 to add, -2 to remove. Rounds to nearest 0.25."
              value={adjustmentValue}
              onChange={(e) => setAdjustmentValue(e.target.value)}
              onBlur={(e) => {
                const val = parseFloat(e.target.value);
                if (!isNaN(val)) {
                  setAdjustmentValue(Math.round(val * 4) / 4);
                }
              }}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">+</InputAdornment>
                  ),
                },
              }}
            />

            <Divider>
              <Chip label="Live Preview" size="small" />
            </Divider>

            <Stack
              direction="row"
              justifyContent="space-between"
              sx={{ px: 1 }}
            >
              <Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                >
                  Current Total
                </Typography>
                <Typography variant="body1" fontWeight="bold">
                  {Number(selectedAssignment?.hours_bought || 0).toFixed(2)} hrs
                </Typography>
              </Box>
              <Box sx={{ textAlign: "right" }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                >
                  New Total
                </Typography>
                <Typography
                  variant="body1"
                  color="primary.main"
                  fontWeight="bold"
                >
                  {(
                    Number(selectedAssignment?.hours_bought || 0) +
                    Math.round(Number(adjustmentValue) * 4) / 4
                  ).toFixed(2)}{" "}
                  hrs
                </Typography>
              </Box>
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}>
          <Button
            onClick={() => {
              setOpenCreditDialog(false);
              setAdjustmentValue(0);
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleUpdateCredits}
            disabled={!adjustmentValue || adjustmentValue === 0}
            disableElevation
          >
            Confirm Update
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog for bulk upload results */}
      <Dialog
        open={bulkUploadDialog.open}
        onClose={() =>
          setBulkUploadDialog((prev) => ({ ...prev, open: false }))
        }
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{bulkUploadDialog.title}</DialogTitle>
        <DialogContent dividers>
          <Alert severity={bulkUploadDialog.severity} sx={{ mb: 2 }}>
            {bulkUploadDialog.message}
          </Alert>

          {bulkUploadDialog.rows.length > 0 && (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: "bold" }}>Row</TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>Student</TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>Tutor</TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>Message</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {bulkUploadDialog.rows.map((row, index) => (
                    <TableRow key={`${row.rowNumber}-${index}`}>
                      <TableCell>{row.rowNumber}</TableCell>
                      <TableCell>{row.studentName || "-"}</TableCell>
                      <TableCell>{row.tutorName || "-"}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={row.status}
                          color={row.status === "Error" ? "error" : "success"}
                        />
                      </TableCell>
                      <TableCell>{row.message}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            variant="contained"
            onClick={() =>
              setBulkUploadDialog((prev) => ({ ...prev, open: false }))
            }
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>

    </Container>
  );
}
